import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatAmount } from "@/utils/formatAmount";
import { useJobSoilPreview, useSetJobSoilCategory } from "../hooks";
import type { SoilPreview, SoilReport, Variant } from "../types";

/** Skrót wariantu, w którym backend policzył kwoty podglądu. */
const VARIANT_SHORT: Record<Variant, string> = {
  min: "min.",
  avg: "śr.",
  max: "maks.",
};

const CATEGORIES = ["I-II", "III", "IV"];

function money(v?: string | null): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : formatAmount(n);
}

function apiDetail(e: unknown): string | undefined {
  return (
    e as { response?: { data?: { errors?: { detail?: string } } } }
  )?.response?.data?.errors?.detail;
}

function pozycje(n: number): string {
  if (n === 1) return "pozycja";
  const d = n % 10;
  const h = n % 100;
  return d >= 2 && d <= 4 && (h < 12 || h > 14) ? "pozycje" : "pozycji";
}

/** Lista maszyn przed/po — dobór sprzętu jest częścią zmiany kategorii. */
function EquipmentList({ items }: { items: string[] }) {
  if (items.length === 0)
    return <span className="text-[var(--color-text-muted)]">brak sprzętu</span>;
  return (
    <span>
      {items.map((m, i) => (
        <span key={`${m}-${i}`} className="block truncate" title={m}>
          {m}
        </span>
      ))}
    </span>
  );
}

/** Kaskadowa zmiana kategorii gruntu z podglądem „przed / po” do zatwierdzenia:
 *  najpierw dry-run (nic nie zapisuje), potem zapis tylko odhaczonych pozycji.
 *  Pozycje bez wariantu (np. ryczałt) są jawnie wylistowane jako pominięte. */
export function SoilCascadeDialog({
  jobId,
  open,
  onClose,
}: {
  jobId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [category, setCategory] = useState("");
  const [preview, setPreview] = useState<SoilPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [report, setReport] = useState<SoilReport | null>(null);
  const [error, setError] = useState("");
  const previewMut = useJobSoilPreview(jobId);
  const applyMut = useSetJobSoilCategory(jobId);

  const reset = () => {
    setCategory("");
    setPreview(null);
    setSelected(new Set());
    setReport(null);
    setError("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const runPreview = (cat: string) => {
    setError("");
    setPreview(null);
    setReport(null);
    previewMut.mutate(
      { category: cat },
      {
        onSuccess: (p) => {
          setPreview(p);
          setSelected(new Set(p.changed.map((c) => c.position)));
        },
        onError: (e: unknown) =>
          setError(apiDetail(e) || "Nie udało się przygotować podglądu."),
      },
    );
  };

  const apply = () => {
    if (!preview || selected.size === 0) return;
    setError("");
    applyMut.mutate(
      { category: preview.target, positionIds: [...selected] },
      {
        onSuccess: ({ meta }) => setReport(meta.soil),
        onError: (e: unknown) =>
          setError(apiDetail(e) || "Nie udało się zmienić kategorii gruntu."),
      },
    );
  };

  // Suma dla zaznaczenia: totals z podglądu minus delty odznaczonych pozycji.
  const selectedTotal = useMemo(() => {
    if (!preview?.totals.old || !preview.totals.new) return null;
    let total = Number(preview.totals.new);
    for (const c of preview.changed) {
      if (selected.has(c.position)) continue;
      total -= Number(c.new.value ?? 0) - Number(c.old?.value ?? 0);
    }
    return Number.isNaN(total) ? null : String(total);
  }, [preview, selected]);

  return (
    <Modal open={open} onClose={close} title="Kategoria gruntu — cała wycena" size="xl">
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-[var(--color-text-secondary)]">
            Docelowa kategoria:
          </span>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`px-2 py-0.5 border text-xs ${
                category === c
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] font-semibold"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
              }`}
              onClick={() => {
                setCategory(c);
                runPreview(c);
              }}
            >
              grunt kat. {c}
            </button>
          ))}
          {previewMut.isPending && (
            <span className="text-xs text-[var(--color-text-muted)]">
              przeliczam podgląd…
            </span>
          )}
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        {preview && !report && (
          <>
            {preview.changed.length === 0 ? (
              <p className="text-xs text-[var(--color-text-muted)]">
                Żadna pozycja nie wymaga zmiany na kat. {preview.target}.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[var(--color-text-muted)]">
                      <th className="px-1 py-1">
                        <input
                          type="checkbox"
                          checked={selected.size === preview.changed.length}
                          onChange={(e) =>
                            setSelected(
                              e.target.checked
                                ? new Set(preview.changed.map((c) => c.position))
                                : new Set(),
                            )
                          }
                        />
                      </th>
                      <th className="px-1 py-1">Lp</th>
                      <th className="px-1 py-1">Opis</th>
                      <th className="px-1 py-1">Podstawa</th>
                      <th className="px-1 py-1">Kategoria</th>
                      <th className="px-1 py-1 text-right">
                        Wartość ({VARIANT_SHORT[preview.variant ?? "avg"]})
                      </th>
                      <th className="px-1 py-1">Sprzęt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changed.map((c) => (
                      <tr
                        key={c.position}
                        className="border-t border-[var(--color-border)] align-top"
                      >
                        <td className="px-1 py-1.5">
                          <input
                            type="checkbox"
                            checked={selected.has(c.position)}
                            onChange={(e) =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(c.position);
                                else next.delete(c.position);
                                return next;
                              })
                            }
                          />
                        </td>
                        <td className="px-1 py-1.5 tabular-nums">{c.lp}</td>
                        <td className="px-1 py-1.5 max-w-64">
                          <span className="line-clamp-2" title={c.description}>
                            {c.description}
                          </span>
                          {c.unmatched_inputs > 0 && (
                            <span className="text-amber-600">
                              ⚠ {c.unmatched_inputs} nakł. bez ceny po zmianie
                            </span>
                          )}
                        </td>
                        <td className="px-1 py-1.5 font-mono whitespace-nowrap">
                          <span className="block text-[var(--color-text-muted)] line-through">
                            {c.old?.basis_raw || "—"}
                          </span>
                          <span className="block">{c.norm_code}</span>
                        </td>
                        <td className="px-1 py-1.5 whitespace-nowrap">
                          {c.old?.soil_category || "—"} →{" "}
                          <strong>{c.new.soil_category}</strong>
                        </td>
                        <td className="px-1 py-1.5 text-right tabular-nums whitespace-nowrap">
                          <span className="block text-[var(--color-text-muted)] line-through">
                            {money(c.old?.value)}
                          </span>
                          <span className="block font-medium">
                            {money(c.new.value)}
                          </span>
                        </td>
                        <td className="px-1 py-1.5 max-w-56">
                          <span className="block text-[var(--color-text-muted)] line-through">
                            <EquipmentList items={c.old?.equipment ?? []} />
                          </span>
                          <EquipmentList items={c.new.equipment} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.skipped.length > 0 && (
              <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <strong>
                  Pominięte ({preview.skipped.length}) — wymagają ręcznej decyzji:
                </strong>
                <ul className="mt-1 space-y-0.5">
                  {preview.skipped.map((s) => (
                    <li key={s.position}>
                      poz. {s.lp}: {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-2">
              <div className="text-xs">
                <span className="text-[var(--color-text-secondary)]">
                  Wartość wyceny ({VARIANT_SHORT[preview.variant ?? "avg"]}):{" "}
                </span>
                <span className="line-through text-[var(--color-text-muted)]">
                  {money(preview.totals.old)} zł
                </span>{" "}
                →{" "}
                <strong>
                  {money(selected.size === preview.changed.length
                    ? preview.totals.new
                    : selectedTotal)}{" "}
                  zł
                </strong>
                <span className="block text-[10px] text-[var(--color-text-muted)]">
                  Podgląd liczony bez dopasowania AI — ceny słabo dobranych
                  nakładów mogą się po zapisie jeszcze doprecyzować.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={close}>
                  Anuluj
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={selected.size === 0}
                  loading={applyMut.isPending}
                  onClick={apply}
                >
                  Zastosuj ({selected.size} {pozycje(selected.size)})
                </Button>
              </div>
            </div>
          </>
        )}

        {report && (
          <div className="space-y-2">
            <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Zmieniono: {report.changed.length}{" "}
              {pozycje(report.changed.length)} na grunt kat. {report.target}
              {report.changed.length > 0 &&
                ` (poz. ${report.changed.map((c) => c.lp).join(", ")})`}
              . Operację można wycofać w „Historii zmian podstaw”.
            </div>
            {report.skipped.length > 0 && (
              <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <strong>Pominięte:</strong>
                <ul className="mt-1 space-y-0.5">
                  {report.skipped.map((s) => (
                    <li key={s.position}>
                      poz. {s.lp}: {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={close}>
                Zamknij
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
